import { Link } from "react-router-dom";
import { useState } from "react";
import { LogoLockup } from "@/components/Logo";
import LiveRadarTicker from "@/components/landing/LiveRadarTicker";
import HeroShowUpCardMock from "@/components/landing/HeroShowUpCardMock";
import NumbersThatRoll from "@/components/landing/NumbersThatRoll";
import TryFingerprintDemo from "@/components/landing/TryFingerprintDemo";
import CrewRolloutStoryboard from "@/components/landing/CrewRolloutStoryboard";
import {
  ArrowRight, Check, CloudSun, Camera, Robot, ClipboardText,
  Package, Snowflake, Users, Buildings, House, ShieldCheck,
  ClockCounterClockwise, FileText, ChatCircleText, Phone,
  Rocket, Sparkle, MapTrifold, Play, X,
} from "@phosphor-icons/react";

/**
 * Field Radar — public marketing landing page.
 * Two-path narrative: Field Maintenance vs Full Property Operations.
 * Design: dark navy + white + teal accent. Mobile-first.
 */
const TEAL = "#14b8a6";
const NAVY = "#0b1220";

const MAINT_FEATURES = [
  { icon: CloudSun,      title: "SweeperRadar™",           body: "Weather-aware dispatch clusters jobs and keeps painters and roofers off rain days." },
  { icon: ClipboardText, title: "InspectRadar™",           body: "Room-by-room move-in / move-out with photo documentation and tenant e-sign." },
  { icon: Robot,         title: "AI Diagnostic Work Orders", body: "Rough voice notes become clean, invoice-ready descriptions with parts and priority." },
  { icon: Camera,        title: "Before / After Proof",    body: "GPS + timestamped photos on every close — court-ready and landlord-shareable." },
  { icon: Package,       title: "Asset Tracking",          body: "HVAC, boilers, appliances, tools — with warranty callback detection built in." },
  { icon: Snowflake,     title: "Snow & Lawn",             body: "Seasonal route sheets, weather triggers, and one-tap crew dispatch." },
];

const OPS_FEATURES = [
  { icon: Users,         title: "Tenant Portals",           body: "TenantRadar™: pay rent, submit tickets, upload lease docs — branded for you." },
  { icon: House,         title: "Landlord Visibility",      body: "Owner portals, statements, quote-approval magic links, portfolio dashboards." },
  { icon: MapTrifold,    title: "Vacancy Monitoring",       body: "Security workflow, camera events, AI damage classification for vacant units." },
  { icon: ShieldCheck,   title: "Compliance",               body: "Philly L&I, licenses, insurance / vendor COI vault, alarm addendums." },
  { icon: FileText,      title: "Lease Renewals + Deposits", body: "Renewal pipeline + itemized deposit reconciliation with e-sign PDFs." },
  { icon: ChatCircleText,title: "Team Collaboration",       body: "Inbox, Team Chat, shared Calendar, and AI Voice Reception for missed calls." },
];

const STEPS = [
  { n: "01", title: "Bring in your crew",   body: "Invite techs, upload your rent roll or property list — or start empty and grow." },
  { n: "02", title: "Radar takes the field", body: "Dispatch jobs, run inspections, capture photo proof, chase nothing." },
  { n: "03", title: "Everyone gets the loop", body: "Landlords see clean owner statements; tenants see live ETAs; office sees everything." },
];

const PLANS = [
  {
    name: "Maintenance",
    tag: "Best for crews and maintenance teams",
    price: "$19–29",
    priceUnit: "per active worker / month",
    altPrice: "or $1.50–2.50 per door / month",
    popular: false,
    features: [
      "SweeperRadar™ (weather-aware dispatch)",
      "InspectRadar™",
      "Work Orders + AI Diagnostic",
      "Before / After photo proof",
      "Assets",
      "Snow & Lawn",
      "GPS clock-in & basic reporting",
    ],
    cta: "Start with Maintenance",
  },
  {
    name: "Operations",
    tag: "Best for property management companies",
    price: "$3–5",
    priceUnit: "per door / month",
    altPrice: "Everything in Maintenance, plus tenant + operations tools",
    popular: true,
    features: [
      "TenantRadar™",
      "Landlord visibility tools",
      "Vacant unit monitoring",
      "Lease Renewals & Deposit Recon",
      "Warranty Tracker",
      "Compliance tools",
      "Delinquency & Evictions tracking",
      "Team Chat, Calendar & AI Voice Reception",
    ],
    cta: "Start with Full Platform",
  },
  {
    name: "Enterprise",
    tag: "For multi-location and larger portfolios",
    price: "Custom",
    priceUnit: "pricing",
    altPrice: "Everything in Operations + priority support",
    popular: false,
    features: [
      "Everything in Operations",
      "Priority support",
      "Custom branding",
      "Advanced integrations",
      "Dedicated onboarding",
    ],
    cta: "Talk to Sales",
  },
];

function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`px-5 sm:px-8 py-20 sm:py-28 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

function ShowcaseShot({ src, label, caption }) {
  return (
    <div className="flex flex-col">
      <div className="border-4 relative shadow-xl overflow-hidden" style={{ borderColor: NAVY }}>
        <img src={src} alt={label} loading="lazy" className="w-full h-auto block"/>
        <div className="absolute top-2 right-2 px-2 py-1 text-[9px] font-black uppercase tracking-widest text-white"
             style={{ backgroundColor: NAVY }}>
          {label}
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-600 leading-snug">{caption}</p>
    </div>
  );
}

/**
 * Demo video block. Reads REACT_APP_DEMO_VIDEO_URL — expects a YouTube, Loom,
 * or Vimeo embed URL (or a direct .mp4). If unset, the play button opens a
 * "book a live demo" fallback modal.
 */
function DemoVideoBlock() {
  const [open, setOpen] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const videoUrl = (process.env.REACT_APP_DEMO_VIDEO_URL || "").trim();

  const stopBubble = (e) => e.stopPropagation();

  const tryDemoLogin = async () => {
    setDemoBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: "demo@fieldradar.pro",
          password: "DemoRadar2026!",
        }),
      });
      if (!r.ok) {
        // Fall back to signup on this env (production doesn't have demo tenant yet)
        window.location.href = "/signup?plan=operations&from=demo-video";
        return;
      }
      const d = await r.json();
      if (d.access_token) localStorage.setItem("access_token", d.access_token);
      window.location.href = "/admin";
    } catch {
      window.location.href = "/signup?plan=operations&from=demo-video";
    } finally {
      setDemoBusy(false);
    }
  };

  return (
    <section id="demo-video" className="px-5 sm:px-8 py-20 sm:py-24 text-white relative overflow-hidden"
             style={{ backgroundColor: NAVY }} data-testid="demo-video-section">
      <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl pointer-events-none"
           style={{ backgroundColor: TEAL }} aria-hidden="true"/>
      <div className="max-w-6xl mx-auto relative grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
        {/* Copy */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 border rounded-full text-xs font-black uppercase tracking-widest"
               style={{ borderColor: TEAL, color: TEAL }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: TEAL }}></span>
            90-second product tour
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight">
            Watch a real Monday morning<br/>
            <span style={{ color: TEAL }}>get dispatched.</span>
          </h2>
          <p className="mt-5 text-base text-slate-300 leading-relaxed">
            Ninety seconds. One tenant call → Claude triage → job dispatched → photo-proofed close → owner email — no clipboards, no lost tickets, no follow-ups you had to remember.
          </p>
          <ul className="mt-6 space-y-2">
            {[
              "Live AI voice triage in action",
              "Dispatch board to phone in the field",
              "Auto-generated owner statement PDF",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-200 leading-snug">
                <Check weight="bold" size={16} style={{ color: TEAL }} className="mt-0.5 flex-shrink-0"/>
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={() => setOpen(true)} data-testid="demo-video-play-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 font-black uppercase text-xs tracking-widest border-2 transition hover:brightness-110"
              style={{ backgroundColor: TEAL, borderColor: TEAL, color: NAVY }}>
              <Play weight="fill" size={16}/> Play the 90-second tour
            </button>
            <button onClick={tryDemoLogin} disabled={demoBusy} data-testid="hero-demo-login-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 font-black uppercase text-xs tracking-widest border-2 border-white/30 text-white hover:bg-white/10 transition disabled:opacity-60">
              <Rocket weight="fill" size={14}/> {demoBusy ? "Signing you in…" : "Or try the live demo"}
            </button>
          </div>
          <div className="mt-4 text-[11px] text-slate-400">
            One click — we sign you into a sample tenant with real seeded data. No card, no email.
          </div>
        </div>
        {/* Poster */}
        <button onClick={() => setOpen(true)}
          data-testid="demo-video-poster"
          className="lg:col-span-3 group relative aspect-video w-full overflow-hidden border-4 shadow-2xl focus:outline-none focus:ring-4"
          style={{ borderColor: TEAL, backgroundColor: "#0f172a" }}
          aria-label="Play 90-second product tour">
          <img src="/screenshots/maint-jobs.jpg" alt=""
               className="absolute inset-0 w-full h-full object-cover object-top opacity-70 group-hover:opacity-80 transition"
               loading="lazy"/>
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${NAVY}CC 0%, transparent 60%)` }}/>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(20,184,166,0.55)] transition group-hover:scale-110"
                 style={{ backgroundColor: TEAL }}>
              <Play weight="fill" size={40} style={{ color: NAVY }}/>
            </div>
          </div>
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <div className="px-2 py-1 text-[9px] font-black uppercase tracking-widest text-white"
                 style={{ backgroundColor: NAVY }}>90 sec</div>
            <div className="px-2 py-1 text-[9px] font-black uppercase tracking-widest text-white bg-red-600 flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              Live product
            </div>
          </div>
          <div className="absolute bottom-3 right-3 hidden sm:block px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white/80 border border-white/20 backdrop-blur">
            Click to play
          </div>
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setOpen(false)} data-testid="demo-video-modal">
          <button onClick={() => setOpen(false)}
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center text-white hover:bg-white/10 rounded-full"
            aria-label="Close">
            <X weight="bold" size={22}/>
          </button>
          <div onClick={stopBubble} className="w-full max-w-4xl">
            {videoUrl ? (
              <div className="aspect-video w-full bg-black border-4" style={{ borderColor: TEAL }}>
                {/\.(mp4|webm|mov)$/i.test(videoUrl) ? (
                  <video src={videoUrl} autoPlay controls playsInline
                         className="w-full h-full object-contain bg-black"
                         data-testid="demo-video-native"/>
                ) : (
                  <iframe src={videoUrl} title="Field Radar demo"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full border-0"
                          data-testid="demo-video-iframe"/>
                )}
              </div>
            ) : (
              <div className="bg-white border-4 p-8 text-slate-900 space-y-4" style={{ borderColor: TEAL }} data-testid="demo-video-fallback">
                <div className="inline-flex items-center gap-2 px-3 py-1 border-2 rounded-full text-[10px] font-black uppercase tracking-widest"
                     style={{ borderColor: TEAL, color: TEAL }}>
                  <Sparkle weight="fill" size={12}/> Recording in edit
                </div>
                <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight" style={{ color: NAVY }}>
                  The 90-second tour is being cut right now.
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  While we finish it, do the one thing that beats any video: <b>one-click into the live demo tenant</b> — real data, all radars unlocked — or grab a 15-minute walk-through with our team.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <button onClick={tryDemoLogin} disabled={demoBusy}
                    data-testid="demo-fallback-try"
                    className="inline-flex flex-col items-start gap-1 p-4 border-2 hover:bg-slate-50 transition text-left disabled:opacity-60"
                    style={{ borderColor: NAVY }}>
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest" style={{ color: TEAL }}>
                      <Rocket weight="fill" size={12}/> Fastest option
                    </div>
                    <div className="font-black text-base" style={{ color: NAVY }}>
                      {demoBusy ? "Signing you in…" : "Try the live demo tenant"}
                    </div>
                    <div className="text-xs text-slate-500 leading-snug">
                      One click — we log you in as <code className="bg-slate-100 px-1 border">demo@fieldradar.pro</code>. No card, no email.
                    </div>
                  </button>
                  <a href="mailto:hello@fieldradar.pro?subject=Field%20Radar%20-%2015-min%20Demo&body=I%27d%20like%20a%2015-minute%20walkthrough."
                    onClick={() => setOpen(false)}
                    data-testid="demo-fallback-book"
                    className="inline-flex flex-col items-start gap-1 p-4 border-2 text-white transition hover:brightness-110"
                    style={{ backgroundColor: NAVY, borderColor: NAVY }}>
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest" style={{ color: TEAL }}>
                      <Sparkle weight="fill" size={12}/> With a human
                    </div>
                    <div className="font-black text-base">Book a 15-min live demo</div>
                    <div className="text-xs opacity-80 leading-snug">Weekdays 9-5 ET · one of our team drives the app.</div>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* NAV */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200" data-testid="landing-nav">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <LogoLockup height={34}/>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm font-semibold text-slate-600">
            <a href="#paths" className="hover:text-slate-900" data-testid="nav-paths">Product</a>
            <a href="#demo-video" className="hover:text-slate-900" data-testid="nav-video">Watch Demo</a>
            <a href="#how" className="hover:text-slate-900" data-testid="nav-how">How It Works</a>
            <a href="#pricing" className="hover:text-slate-900" data-testid="nav-pricing">Pricing</a>
            <a href="#demo" className="hover:text-slate-900" data-testid="nav-demo">Book a Demo</a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline-flex h-10 px-4 text-sm font-black uppercase tracking-widest text-slate-700 hover:text-slate-900 items-center" data-testid="nav-signin">Sign In</Link>
            <Link to="/signup"
              className="inline-flex h-10 px-4 text-sm font-black uppercase tracking-widest text-white items-center border-2 hover:brightness-110 transition"
              style={{ backgroundColor: NAVY, borderColor: NAVY }}
              data-testid="nav-start-free">
              Start Free
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <Section id="hero" className="relative overflow-hidden">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full opacity-10 blur-3xl pointer-events-none"
             style={{ backgroundColor: TEAL }} aria-hidden="true"/>
        <div className="relative">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 border rounded-full text-xs font-black uppercase tracking-widest"
               style={{ borderColor: TEAL, color: TEAL }} data-testid="hero-badge">
            <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: TEAL }}></span>
            For property managers &amp; maintenance teams who still run trucks
          </div>
          <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
              style={{ color: NAVY }} data-testid="hero-headline">
            Property Operations<br/>
            <span style={{ color: TEAL }}>Command Center</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl" data-testid="hero-sub">
            Run maintenance, inspections, tenants, and compliance from one platform — or start with just the field tools your crews will actually use.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link to="/signup"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 font-black uppercase text-xs tracking-widest text-white border-2 hover:brightness-110 transition w-full sm:w-auto"
              style={{ backgroundColor: NAVY, borderColor: NAVY }}>
              Start Free — 60 Seconds <ArrowRight weight="bold" size={16}/>
            </Link>
            <a href="#demo"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 font-black uppercase text-xs tracking-widest border-2 bg-white hover:bg-slate-50 transition w-full sm:w-auto"
              style={{ color: NAVY, borderColor: NAVY }}>
              Book a 15-min Demo
            </a>
          </div>
          <div className="mt-6 text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="hero-trust">
            <span>Works with AppFolio &amp; Buildium</span>
            <span className="text-slate-300">·</span>
            <span>Cancel anytime</span>
            <span className="text-slate-300">·</span>
            <span>Built by people who still run trucks</span>
          </div>
          <div className="mt-6">
            <LiveRadarTicker/>
          </div>
        </div>
        <div className="absolute right-6 top-14 hidden lg:block w-96">
          <HeroShowUpCardMock/>
        </div>
      </Section>

      <NumbersThatRoll/>
      <CrewRolloutStoryboard/>
      <TryFingerprintDemo/>

      {/* TWO-PATH */}
      <Section id="paths" className="bg-slate-50">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: TEAL }}>Two ways to start</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight" style={{ color: NAVY }}>
            Choose your starting point
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
          {/* Card A: Maintenance */}
          <div className="p-8 bg-white border-2 flex flex-col" style={{ borderColor: "#e2e8f0" }} data-testid="path-maintenance">
            <div className="w-12 h-12 flex items-center justify-center mb-5 rounded-sm" style={{ backgroundColor: `${TEAL}22`, color: TEAL }}>
              <CloudSun weight="fill" size={26}/>
            </div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">Path A</div>
            <h3 className="font-display font-black text-2xl tracking-tight" style={{ color: NAVY }}>Field Maintenance</h3>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Everything your crews need in the field: weather-aware dispatch, inspections, AI work orders, photo proof, and asset tracking.
            </p>
            <p className="mt-4 text-xs font-semibold text-slate-500">
              <span className="font-black uppercase tracking-widest text-slate-700">Best for:</span> Maintenance teams and smaller operators
            </p>
            <div className="flex-1"/>
            <Link to="/signup?plan=maintenance"
              data-testid="path-maintenance-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 h-12 px-4 font-black uppercase text-xs tracking-widest border-2 bg-white hover:bg-slate-50 transition"
              style={{ color: NAVY, borderColor: NAVY }}>
              Start with Maintenance <ArrowRight weight="bold" size={14}/>
            </Link>
          </div>
          {/* Card B: Operations */}
          <div className="p-8 bg-white border-2 flex flex-col relative shadow-xl" style={{ borderColor: TEAL }} data-testid="path-operations">
            <div className="absolute -top-3 left-8 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white"
                 style={{ backgroundColor: TEAL }}>
              Most Popular
            </div>
            <div className="w-12 h-12 flex items-center justify-center mb-5 rounded-sm" style={{ backgroundColor: `${TEAL}22`, color: TEAL }}>
              <Buildings weight="fill" size={26}/>
            </div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">Path B</div>
            <h3 className="font-display font-black text-2xl tracking-tight" style={{ color: NAVY }}>Property Operations</h3>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              All the field tools plus tenant portals, landlord visibility, vacancy monitoring, compliance, lease renewals, and team collaboration.
            </p>
            <p className="mt-4 text-xs font-semibold text-slate-500">
              <span className="font-black uppercase tracking-widest text-slate-700">Best for:</span> Property management companies
            </p>
            <div className="flex-1"/>
            <Link to="/signup?plan=operations"
              data-testid="path-operations-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 h-12 px-4 font-black uppercase text-xs tracking-widest text-white border-2 hover:brightness-110 transition"
              style={{ backgroundColor: NAVY, borderColor: NAVY }}>
              Start with Full Platform <ArrowRight weight="bold" size={14}/>
            </Link>
          </div>
        </div>
        <p className="mt-6 text-sm text-center text-slate-500 max-w-xl mx-auto" data-testid="paths-footnote">
          You can always upgrade later. No need to buy everything on day one.
        </p>
      </Section>

      {/* DEMO VIDEO */}
      <DemoVideoBlock/>

      {/* MAINTENANCE FEATURES */}
      <Section id="maintenance" className="bg-white">
        <div className="max-w-3xl mb-10">
          <div className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: TEAL }}>Field Maintenance</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight" style={{ color: NAVY }}>
            The field tools your crew will actually use
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {MAINT_FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} data-testid={`maint-feature-${i}`}
                className="p-6 bg-white border border-slate-200 hover:border-slate-900 transition group flex flex-col">
                <div className="w-11 h-11 flex items-center justify-center mb-5 rounded-sm" style={{ backgroundColor: `${TEAL}22`, color: TEAL }}>
                  <Icon weight="fill" size={22}/>
                </div>
                <h3 className="font-display font-black text-base tracking-tight mb-2" style={{ color: NAVY }}>{f.title}</h3>
                <p className="text-sm text-slate-600 leading-snug">{f.body}</p>
              </div>
            );
          })}
        </div>
        {/* Real product screenshot */}
        <div className="mt-12" data-testid="maint-screenshot">
          <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: TEAL }}></span>
            Real screenshot · not a mockup
          </div>
          <div className="border-4 relative shadow-2xl overflow-hidden" style={{ borderColor: NAVY }}>
            <img src="/screenshots/maint-jobs.jpg" alt="Field Radar Jobs board with life-safety flagging"
                 loading="lazy" className="w-full h-auto block"/>
            <div className="absolute top-3 right-3 px-2 py-1 text-[9px] font-black uppercase tracking-widest text-white"
                 style={{ backgroundColor: NAVY }}>
              Jobs · Live Field
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500 max-w-xl">
            Every work order tagged with safety flags, dispatch status, and assignment — with AI-bulk actions to close out
            a whole property in one click.
          </p>
        </div>
      </Section>

      {/* OPERATIONS FEATURES */}
      <Section id="operations" className="bg-slate-50">
        <div className="max-w-3xl mb-10">
          <div className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: TEAL }}>Full Property Operations</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight" style={{ color: NAVY }}>
            Everything else your office does today — under one roof
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {OPS_FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} data-testid={`ops-feature-${i}`}
                className="p-6 bg-white border border-slate-200 hover:border-slate-900 transition group flex flex-col">
                <div className="w-11 h-11 flex items-center justify-center mb-5 rounded-sm" style={{ backgroundColor: `${NAVY}12`, color: NAVY }}>
                  <Icon weight="fill" size={22}/>
                </div>
                <h3 className="font-display font-black text-base tracking-tight mb-2" style={{ color: NAVY }}>{f.title}</h3>
                <p className="text-sm text-slate-600 leading-snug">{f.body}</p>
              </div>
            );
          })}
        </div>
        {/* Two showcase screenshots stacked / side-by-side */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="ops-screenshots">
          <ShowcaseShot src="/screenshots/ops-voice.jpg"
            label="AI Voice Reception"
            caption="Missed call transcript → Claude triage → auto-created job. Ships with sample transcripts you can run right now."/>
          <ShowcaseShot src="/screenshots/ops-delinquency.jpg"
            label="Delinquency Radar"
            caption="Aging buckets, one-tap payment reminders, ACH pull, and one-click escalate to eviction."/>
          <ShowcaseShot src="/screenshots/ops-calendar.jpg"
            label="Team Calendar"
            caption="Court dates, on-call shifts, vacation, and evictions filtered by Management / Maintenance / Combined."/>
          <ShowcaseShot src="/screenshots/maint-seasonal.jpg"
            label="Snow & Lawn"
            caption="Vendor routes, per-visit logs, and weather-triggered dispatch for LCV / Lawn Guru style crews."/>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how" className="bg-white">
        <div className="max-w-3xl mb-12">
          <div className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: TEAL }}>How it works</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight" style={{ color: NAVY }}>
            From signup to shipped work in a Monday morning
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" data-testid="how-steps">
          {STEPS.map((s, i) => (
            <div key={s.n} className="p-6 bg-white border-2 flex flex-col" style={{ borderColor: "#e2e8f0" }} data-testid={`how-step-${i}`}>
              <div className="font-display font-black text-4xl" style={{ color: TEAL }}>{s.n}</div>
              <h3 className="mt-3 font-display font-black text-lg tracking-tight" style={{ color: NAVY }}>{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-snug">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PRICING */}
      <Section id="pricing" className="bg-slate-50">
        <div className="max-w-3xl">
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight" style={{ color: NAVY }}>
            Simple pricing that scales with you
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Start with field maintenance or run your entire property operations from one platform.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 items-stretch">
          {PLANS.map((p) => (
            <div key={p.name}
              data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`relative p-7 bg-white border-2 flex flex-col h-full ${p.popular ? "shadow-xl" : ""}`}
              style={{ borderColor: p.popular ? TEAL : "#e2e8f0" }}>
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white"
                  style={{ backgroundColor: TEAL }}>
                  Most Popular
                </div>
              )}
              <h3 className="font-display font-black text-2xl tracking-tight" style={{ color: NAVY }}>{p.name}</h3>
              <p className="text-xs text-slate-500 mt-1 leading-snug min-h-[2.4em]">{p.tag}</p>
              <div className="mt-6 flex items-baseline gap-1 flex-wrap">
                <span className="font-display font-black text-4xl" style={{ color: NAVY }}>{p.price}</span>
                <span className="text-slate-500 text-xs font-semibold">{p.priceUnit}</span>
              </div>
              <div className="mt-2 text-[11px] text-slate-500 leading-snug">{p.altPrice}</div>
              <ul className="mt-6 space-y-2">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 leading-snug">
                    <Check weight="bold" size={14} style={{ color: TEAL }} className="mt-1 flex-shrink-0"/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="flex-1"/>
              <Link to={p.name === "Enterprise" ? "#demo" : `/signup?plan=${p.name.toLowerCase()}`}
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className={`mt-8 inline-flex items-center justify-center gap-2 h-12 px-4 font-black uppercase text-xs tracking-widest border-2 transition ${p.popular ? "text-white hover:brightness-110" : "hover:bg-slate-50"}`}
                style={{
                  backgroundColor: p.popular ? NAVY : "white",
                  borderColor: NAVY,
                  color: p.popular ? "white" : NAVY,
                }}>
                {p.cta} <ArrowRight weight="bold" size={14}/>
              </Link>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="pricing-footnote">
          <span>7-day free trial</span>
          <span className="text-slate-300">·</span>
          <span>Cancel anytime</span>
          <span className="text-slate-300">·</span>
          <span>No long-term contracts required</span>
        </p>
      </Section>

      {/* FINAL CTA */}
      <Section id="demo" className="text-white relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundColor: NAVY }} aria-hidden="true"/>
        <div className="absolute -bottom-32 -right-10 w-[540px] h-[540px] rounded-full opacity-20 blur-3xl pointer-events-none"
             style={{ backgroundColor: TEAL }} aria-hidden="true"/>
        <div className="relative max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 border rounded-full text-xs font-black uppercase tracking-widest"
               style={{ borderColor: TEAL, color: TEAL }}>
            <Sparkle weight="fill" size={12}/> Live demo, real data
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
            See Field Radar run<br/>your operation
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
            Load a few of your real work orders or one weekend's rent roll. Watch dispatch → field → photo-proofed close happen live in 15 minutes.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <a href="mailto:hello@fieldradar.pro?subject=Field%20Radar%20-%20Demo%20Request&body=I%27d%20like%20to%20book%20a%2015-minute%20demo."
              data-testid="final-cta-demo"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 font-black uppercase text-xs tracking-widest border-2 transition hover:brightness-110 w-full sm:w-auto"
              style={{ backgroundColor: TEAL, borderColor: TEAL, color: NAVY }}>
              Book a 15-Minute Demo <ArrowRight weight="bold" size={16}/>
            </a>
            <Link to="/signup"
              data-testid="final-cta-signup"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 font-black uppercase text-xs tracking-widest border-2 border-white/40 text-white bg-transparent hover:bg-white/10 transition w-full sm:w-auto">
              <Rocket weight="fill" size={14}/> Start Free
            </Link>
            <a href="tel:+14454472327"
              data-testid="final-cta-phone"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 font-black uppercase text-xs tracking-widest border-2 border-white/40 text-white bg-transparent hover:bg-white/10 transition w-full sm:w-auto">
              <Phone weight="fill" size={14}/> (445) 447-2327
            </a>
          </div>
          <div className="mt-4 text-xs text-slate-400 font-semibold tracking-wider">
            Or dial <span className="text-white">445-44RADAR</span> · Email <a href="mailto:hello@fieldradar.pro" className="text-white hover:underline">hello@fieldradar.pro</a>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="bg-white border-t border-slate-200" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <LogoLockup height={30}/>
            <div className="text-xs text-slate-500 mt-1">Property Operations Command Center</div>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600 font-semibold">
            <a href="#paths"       className="hover:text-slate-900">Product</a>
            <a href="#how"         className="hover:text-slate-900">How It Works</a>
            <a href="#pricing"     className="hover:text-slate-900">Pricing</a>
            <a href="#demo"        className="hover:text-slate-900">Book a Demo</a>
            <Link to="/trust"      className="hover:text-slate-900 inline-flex items-center gap-1"
                                   data-testid="footer-trust-link"
                                   title="Security posture, compliance, and IT setup — for procurement teams">
              <span className="text-[#00C4CC]">🛡️</span> Security
            </Link>
            <Link to="/login"      className="hover:text-slate-900">Sign In</Link>
            <a href="tel:+14454472327" className="hover:text-slate-900" data-testid="footer-phone">(445) 447-2327</a>
            <a href="mailto:hello@fieldradar.pro" className="hover:text-slate-900" data-testid="footer-email">hello@fieldradar.pro</a>
          </div>
          <div className="text-xs text-slate-400">&copy; 2026 Field Radar</div>
        </div>
      </footer>
    </div>
  );
}
